import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Phone, Plane, User, Send, MessageSquare, RefreshCw } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FLOW = ["confirmed", "assigned", "arrived", "on_board", "completed"];

export default function BookingDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [location, setLocation] = useState(null);
  const [concierge, setConcierge] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [refund, setRefund] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [retForm, setRetForm] = useState({ date: "", time: "18:00", flight: "" });
  const [retBusy, setRetBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings/mine");
      const b = (data.bookings || []).find((x) => x.booking_id === id);
      setBooking(b || null);
      setNotFound(!b);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!booking) return;
    api
      .get(`/bookings/${id}/concierge`)
      .then(({ data }) => setConcierge(data))
      .catch(() => {});

    const trackable = ["assigned", "en_route", "arrived", "on_board"].includes(booking.status);
    if (!trackable) {
      setLocation(null);
      return;
    }
    let timer;
    const poll = () =>
      api
        .get(`/bookings/${id}/driver-location`)
        .then(({ data }) => setLocation(data))
        .catch((e) => {
          setLocation(null);
          if (e?.response?.status === 404 && timer) clearInterval(timer);
        });
    poll();
    timer = setInterval(poll, 20000);
    return () => clearInterval(timer);
  }, [id, booking]);

  useEffect(() => {
    if (!booking) return;
    const loadMsgs = () =>
      api.get(`/bookings/${id}/messages`).then(({ data }) => setMessages(data.messages || [])).catch(() => {});
    loadMsgs();
    const timer = setInterval(loadMsgs, 15000);
    return () => clearInterval(timer);
  }, [id, booking]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = msgText.trim();
    if (!text) return;
    setMsgText("");
    try {
      await api.post(`/bookings/${id}/messages`, { text });
      const { data } = await api.get(`/bookings/${id}/messages`);
      setMessages(data.messages || []);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openCancel = async () => {
    setShowCancel(true);
    setRefund(null);
    try {
      const { data } = await api.get(`/bookings/${id}/refund-quote`);
      setRefund(data);
    } catch {
      setRefund({ error: true });
    }
  };

  const cancel = async () => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success(t("bookings.cancelled"));
      setShowCancel(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openReturnForm = () => {
    if (booking.return_pickup_at && booking.return_status !== "cancelled") {
      const [d, tm] = booking.return_pickup_at.split("T");
      setRetForm({ date: d, time: (tm || "").slice(0, 5) || "18:00", flight: booking.return_flight_number || "" });
    } else {
      setRetForm({ date: "", time: "18:00", flight: "" });
    }
    setShowReturnForm(true);
  };

  const saveReturn = async (method) => {
    if (!retForm.date || !retForm.time) return toast.error(t("search.errRet"));
    setRetBusy(true);
    try {
      await api[method](`/bookings/${id}/return`, {
        return_pickup_at: `${retForm.date}T${retForm.time}:00`,
        return_flight_number: retForm.flight || null,
      });
      toast.success(t("bookings.returnSaved"));
      setShowReturnForm(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setRetBusy(false);
  };

  const cancelReturn = async () => {
    if (!window.confirm(t("bookings.returnCancelConfirm"))) return;
    setRetBusy(true);
    try {
      await api.delete(`/bookings/${id}/return`);
      toast.success(t("bookings.returnCancelled"));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setRetBusy(false);
  };

  const syncFlight = async () => {
    setRetBusy(true);
    try {
      await api.post(`/bookings/${id}/flight-sync`);
      toast.success(t("bookings.flightSynced"));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setRetBusy(false);
  };

  if (!booking)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-4xl px-5 py-20">
          <p className="text-slate-500" data-testid={notFound ? "booking-not-found" : "booking-loading"}>
            {notFound ? t("bookings.empty") : t("common.loading")}
          </p>
          {notFound && (
            <Link to="/bookings" className="mt-4 inline-block text-sm font-semibold text-sea underline">
              {t("bookings.back")}
            </Link>
          )}
        </div>
      </div>
    );

  const stepIndex = FLOW.indexOf(booking.status);
  const tips = Array.isArray(concierge?.tips) ? concierge.tips : [];
  const conciergeText =
    typeof concierge === "string"
      ? concierge
      : [concierge?.text, concierge?.message, concierge?.guide].find((v) => typeof v === "string") || "";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-12 lg:px-10 lg:py-16">
        <button
          data-testid="back-to-bookings-btn"
          onClick={() => navigate("/bookings")}
          className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors duration-200 hover:text-sea"
        >
          <ArrowLeft size={15} /> {t("bookings.back")}
        </button>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">
              {t("bookings.code")} {booking.code} · PIN {booking.pin}
            </p>
            <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="booking-detail-route">
              {booking.origin?.name} <ArrowRight size={20} className="inline text-slate-300" />{" "}
              {booking.destination?.name}
            </h1>
          </div>
          <StatusBadge status={booking.status} testId="booking-detail-status" />
        </div>

        <div className="mt-10 grid gap-8 md:grid-cols-12">
          <div className="space-y-6 md:col-span-7">
            <section className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("bookings.timeline")}</p>
              <ol className="mt-5 space-y-4" data-testid="status-timeline">
                {FLOW.map((s, i) => (
                  <li key={s} className="flex items-center gap-3">
                    <span
                      className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold ${
                        booking.status === "cancelled"
                          ? "bg-slate-100 text-slate-400"
                          : i <= stepIndex
                            ? "bg-sea text-white"
                            : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span
                      className={`text-sm ${
                        i <= stepIndex && booking.status !== "cancelled"
                          ? "font-medium text-slate-900"
                          : "text-slate-400"
                      }`}
                    >
                      {t(`status.${s}`)}
                    </span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("bookings.track")}</p>
              <Link
                to={`/trip/${booking.code}`}
                data-testid="open-live-map-link"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-sea px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sea-dark"
              >
                <MapPin size={15} /> {t("live.openMap")}
              </Link>
              {location?.latitude ? (
                <div className="mt-4" data-testid="driver-location">
                  <p className="text-sm text-slate-700">
                    <MapPin size={14} className="mr-1.5 inline text-sea" />
                    {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                  </p>
                  <a
                    className="mt-3 inline-block text-sm font-semibold text-sea underline"
                    href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google Maps
                  </a>
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500" data-testid="driver-location-empty">
                  {t("bookings.noLocation")}
                </p>
              )}
            </section>

            {(tips.length > 0 || conciergeText) && (
              <section className="rounded-xl border border-slate-200 bg-white p-6 ambient">
                <p className="label-eyebrow">{t("bookings.concierge")}</p>
                {conciergeText && (
                  <p
                    className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-700"
                    data-testid="concierge-text"
                  >
                    {conciergeText}
                  </p>
                )}
                {tips.length > 0 && (
                  <ul className="mt-4 space-y-4" data-testid="concierge-tips">
                    {tips.map((tip, i) => (
                      <li key={tip.title || i} data-testid={`concierge-tip-${i}`}>
                        <p className="text-sm font-semibold text-slate-900">{tip.title}</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{tip.detail}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            <section className="rounded-xl border border-slate-200 bg-white p-6 ambient" data-testid="trip-chat">
              <p className="label-eyebrow flex items-center gap-2">
                <MessageSquare size={13} className="text-sea" /> {t("ops.messages")}
              </p>
              <div className="mt-4 max-h-72 space-y-3 overflow-y-auto" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-sm text-slate-400">{t("ops.noMessages")}</p>}
                {messages.map((m) => {
                  const mine = m.sender === "guest" || m.sender === "customer";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-sea text-white" : "bg-slate-100 text-slate-800"}`}>
                        {!mine && <p className="mb-0.5 text-[10px] font-semibold opacity-70">{m.sender_name || t("bookings.driver")}</p>}
                        <p className="leading-snug">{m.text_translated || m.text_original || m.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={sendMessage} className="mt-4 flex gap-2">
                <Input
                  data-testid="chat-input"
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  placeholder={t("ops.typeMsg")}
                />
                <Button type="submit" data-testid="chat-send-btn" className="shrink-0 bg-sea hover:bg-sea-dark" disabled={!msgText.trim()}>
                  <Send size={15} />
                </Button>
              </form>
            </section>
          </div>

          <aside className="space-y-6 md:col-span-5">
            <div className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("checkout.pickup")}</dt>
                  <dd className="font-medium">{booking.pickup_at?.replace("T", " · ").slice(0, 18)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("checkout.vehicle")}</dt>
                  <dd className="font-medium">{booking.vehicle_class_name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("checkout.guests")}</dt>
                  <dd className="font-medium">
                    {booking.pax} · {booking.luggage} {t("results.bags")}
                  </dd>
                </div>
                {booking.flight_number && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">{t("bookings.flight")}</dt>
                    <dd className="font-medium">
                      <Plane size={13} className="mr-1 inline" />
                      {booking.flight_number}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("checkout.payment")}</dt>
                  <dd className="font-medium">{t("checkout.cash")}</dd>
                </div>
              </dl>
              <div className="mt-5 flex items-end justify-between border-t border-slate-200 pt-4">
                <span className="text-sm text-slate-500">{t("bookings.price")}</span>
                <span className="font-display text-2xl text-sea">{eur(booking.price_eur)}</span>
              </div>
            </div>

            {!["cancelled"].includes(booking.status) && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 ambient" data-testid="return-card">
                <p className="label-eyebrow flex items-center gap-2">
                  <RefreshCw size={13} className="text-sea" /> {t("bookings.returnTrip")}
                </p>
                {booking.return_pickup_at && booking.return_status !== "cancelled" && !showReturnForm ? (
                  <div className="mt-4 space-y-2 text-sm" data-testid="return-info">
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t("bookings.returnPickup")}</span>
                      <span className="font-medium">{booking.return_pickup_at.replace("T", " · ").slice(0, 18)}</span>
                    </div>
                    {booking.return_flight_number && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">{t("bookings.flight")}</span>
                        <span className="font-medium"><Plane size={13} className="mr-1 inline" />{booking.return_flight_number}</span>
                      </div>
                    )}
                    {booking.return_price_eur != null && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">{t("bookings.price")}</span>
                        <span className="font-medium">{eur(booking.return_price_eur)}</span>
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" data-testid="edit-return-btn" onClick={openReturnForm}>{t("bookings.editReturn")}</Button>
                      <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="cancel-return-btn" disabled={retBusy} onClick={cancelReturn}>{t("bookings.cancelReturn")}</Button>
                    </div>
                  </div>
                ) : showReturnForm ? (
                  <div className="mt-4 space-y-3" data-testid="return-form">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-slate-500">{t("search.retDate")}</label>
                        <Input type="date" data-testid="return-date-input" value={retForm.date} min={booking.pickup_at?.slice(0, 10)} onChange={(e) => setRetForm({ ...retForm, date: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">{t("search.retTime")}</label>
                        <Input type="time" data-testid="return-time-input" value={retForm.time} onChange={(e) => setRetForm({ ...retForm, time: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">{t("bookings.returnFlight")}</label>
                      <Input data-testid="return-flight-input" value={retForm.flight} onChange={(e) => setRetForm({ ...retForm, flight: e.target.value })} className="mt-1" placeholder="TK2402" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-sea hover:bg-sea-dark" disabled={retBusy} data-testid="save-return-btn" onClick={() => saveReturn(booking.return_pickup_at && booking.return_status !== "cancelled" ? "patch" : "post")}>
                        {retBusy ? t("common.loading") : t("bookings.saveReturn")}
                      </Button>
                      <Button size="sm" variant="outline" data-testid="return-form-cancel" onClick={() => setShowReturnForm(false)}>{t("ops.keep")}</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="mt-4" data-testid="add-return-btn" onClick={openReturnForm}>{t("bookings.addReturn")}</Button>
                )}
                {booking.flight_number && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <Button size="sm" variant="outline" disabled={retBusy} data-testid="flight-sync-btn" onClick={syncFlight}>
                      <RefreshCw size={13} className="mr-1.5" />{t("bookings.syncFlight")}
                    </Button>
                    <p className="mt-1.5 text-xs text-slate-400">{t("bookings.syncFlightHint")}</p>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("bookings.driver")}</p>
              {booking.driver ? (
                <div className="mt-4 space-y-2 text-sm" data-testid="driver-info">
                  <p className="font-medium">
                    <User size={14} className="mr-1.5 inline text-sea" />
                    {booking.driver.name}
                  </p>
                  {booking.driver.phone && (
                    <a href={`tel:${booking.driver.phone}`} className="block text-sea underline">
                      <Phone size={13} className="mr-1.5 inline" />
                      {booking.driver.phone}
                    </a>
                  )}
                  {booking.driver.vehicle && <p className="text-slate-600">{booking.driver.vehicle}</p>}
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">{t("bookings.noDriver")}</p>
              )}
            </div>

            {!["completed", "cancelled"].includes(booking.status) && (
              <Button
                data-testid="cancel-booking-btn"
                variant="outline"
                onClick={openCancel}
                className="w-full border-destructive/40 text-destructive transition-colors duration-200 hover:bg-destructive/5"
              >
                {t("bookings.cancel")}
              </Button>
            )}
            <Link to="/support" className="block text-center text-sm font-semibold text-sea underline">
              {t("support.title")}
            </Link>
          </aside>
        </div>
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 p-5" data-testid="cancel-modal" onClick={() => setShowCancel(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 ambient" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-xl tracking-tight">{t("ops.refundTitle")}</p>
            {refund ? (
              refund.error ? (
                <p className="mt-4 text-sm text-slate-500" data-testid="refund-error">{t("ops.noRefund")}</p>
              ) : (
              <div className="mt-4 space-y-2 text-sm text-slate-700" data-testid="refund-quote">
                {refund.free_cancellation ? (
                  <p className="rounded-lg bg-emerald-50 px-3 py-2 font-medium text-emerald-700">{t("ops.refundFree")}</p>
                ) : (
                  <p>
                    {t("ops.refundLine")}: <b>{refund.percent}%</b>
                    {refund.paid ? ` · ${eur(refund.amount)}` : ""}
                  </p>
                )}
                {typeof refund.hours_before_pickup === "number" && refund.hours_before_pickup > 0 && (
                  <p className="text-xs text-slate-500">{Math.round(refund.hours_before_pickup)}h before pickup</p>
                )}
                {!refund.paid && !refund.free_cancellation && <p className="text-xs text-slate-500">{t("ops.noRefund")}</p>}
              </div>
              )
            ) : (
              <p className="mt-4 text-sm text-slate-500">{t("common.loading")}</p>
            )}
            <div className="mt-6 flex gap-3">
              <Button variant="outline" className="flex-1" data-testid="cancel-keep-btn" onClick={() => setShowCancel(false)}>
                {t("ops.keep")}
              </Button>
              <Button
                className="flex-1 bg-destructive text-white hover:bg-destructive/90"
                data-testid="cancel-confirm-btn"
                onClick={cancel}
              >
                {t("ops.confirmCancel")}
              </Button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
